import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import logo from './logo.png';
import './Header.css';

export default function Header() {
  const username = localStorage.getItem('username');
  const role = localStorage.getItem('role');
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const buttonRefs = React.useRef([]);

  // Add body scroll lock when menu is open
  React.useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('username');
    navigate('/login');
  };

  const allMenuItems = [
    {
      label: 'Wells',
      items: [
        { path: '/', label: 'Well List' },
        { path: '/bha-v2', label: 'BHA' },
        { path: '/casing-v2', label: 'Casing' },
        { path: '/deviation-v2', label: 'Deviation' }
      ]
    },
    {
      label: 'Reports',
      items: [
        { path: '/ddr', label: 'Daily Drilling Report' }
      ]
    },
    {
      label: 'Analytics',
      items: [
        { path: '/welldata', label: 'Well Dashboard' }
      ]
    },
    {
      label: 'Data Admin',
      items: [
        { path: '/attachments', label: 'Attachments' },
        { path: '/pob', label: ' List POB' },
        { path: '/jobcodes', label: 'List Job Codes' },
      ]
    },
    {
      label: 'Sys Admin',
      items: [
        { path: '/configuration', label: 'Table Configuration' },
        { path: '/admin/ddr-layout', label: 'DDR Layout Manager' },        
        { path: '/analysis', label: 'Data Count' },
        { path: '/admin/users', label: 'User Management' },
        { path: '/qdrant-manager', label: 'Qdrant Vector DB' },
        { path: '/ai-agent', label: 'AI Assistant' }
      ]
    }
  ];

  // Filter menu items based on user role
  const menuItems = allMenuItems.filter(group => {
    if (group.label === 'Admin') {
      return role === 'admin';
    }
    return true;
  });

  const isActive = (path) => location.pathname === path;

  // Toggle dropdown menu
  const toggleDropdown = (groupIndex) => {
    if (activeDropdown === groupIndex) {
      setActiveDropdown(null);
    } else {
      const button = buttonRefs.current[groupIndex];
      if (button) {
        const rect = button.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + 5,
          left: rect.left
        });
      }
      setActiveDropdown(groupIndex);
    }
  };

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = () => {
      setActiveDropdown(null);
    };
    
    if (activeDropdown !== null) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [activeDropdown]);

  return (
    <header className="header-container">
      <div className="header-bar">
        <div className="header-left">
          <img src={logo} alt="Logo" className="logo" />
          <div className="company-info">
            <h1 className="app-title">Drilling Data</h1>
            <div className="breadcrumb">
              {menuItems.map(group => 
                group.items.find(item => isActive(item.path))?.label
              ).filter(Boolean).join(' / ')}
            </div>
          </div>
        </div>

        <div className="header-right">
          <nav className="main-nav">
            {menuItems.map((group, index) => (
              <div key={index} className="nav-group">
                <button 
                  ref={el => buttonRefs.current[index] = el}
                  className="nav-group-label"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleDropdown(index);
                  }}
                >
                  {group.label}
                  <span className={`dropdown-arrow ${activeDropdown === index ? 'open' : ''}`}>▼</span>
                </button>
              </div>
            ))}
            
            {/* Render active dropdown with fixed positioning */}
            {activeDropdown !== null && (
              <div 
                className="nav-group-items active fixed-dropdown"
                style={{
                  position: 'fixed',
                  top: `${dropdownPosition.top}px`,
                  left: `${dropdownPosition.left}px`
                }}
              >
                {menuItems[activeDropdown].items.map((item, itemIndex) => (
                  <Link
                    key={itemIndex}
                    to={item.path}
                    className={`nav-link ${isActive(item.path) ? 'active' : ''}`}
                    onClick={() => setActiveDropdown(null)}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            )}
          </nav>

          <div className="user-section">
            <div className="user-info">
              <span className="username">{username}</span>
              <span className="role">{role}</span>
            </div>
            <button className="logout-button" onClick={handleLogout}>
              Logout
            </button>
          </div>
        </div>

        {/* Mobile menu button */}
        <button 
          className="mobile-menu-button"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-expanded={isMenuOpen}
          aria-label="Toggle menu"
        >
          <span className="menu-icon">Menu</span>
        </button>
      </div>

      {/* Mobile navigation */}
      <div 
        className={`mobile-nav ${isMenuOpen ? 'open' : ''}`}
        aria-hidden={!isMenuOpen}
      >
        {menuItems.map((group, index) => (
          <div key={index} className="mobile-nav-group">
            <h3>{group.label}</h3>
            {group.items.map((item, itemIndex) => (
              <Link
                key={itemIndex}
                to={item.path}
                className={`mobile-nav-link ${isActive(item.path) ? 'active' : ''}`}
                onClick={() => setIsMenuOpen(false)}
              >
                {item.label}
              </Link>
            ))}
          </div>
        ))}
        <div className="mobile-user-info">
          <span>{username} ({role})</span>
          <button 
            className="mobile-logout-button" 
            onClick={() => {
              setIsMenuOpen(false);
              handleLogout();
            }}
          >
            Logout
          </button>
        </div>
      </div>
    </header>
  );
}